import LogoutIcon from '@mui/icons-material/Logout'
import {
  Avatar,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Box as MuiBox,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Kenneth } from '@/components/ui'
import { useAuthStore } from '@/stores/authStore'
import { getInitials } from '@/utils/nameUtils'

// The middle header column. Takes the row's free space so the gaps either side of
// it stay equal to the one the `pageRow` variant puts between every column, and is
// capped so a wide viewport doesn't stretch the search box across the whole bar.
const SearchColumn = styled(MuiBox)({
  flex: 1,
  minWidth: 0,
  maxWidth: 420,
})

const UserMenuHeader = styled(MuiBox)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  minWidth: 220,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  pointerEvents: 'none',
}))

interface AppHeaderProps {
  title?: React.ReactNode
  subtitle?: React.ReactNode
  search?: React.ReactNode
  actions?: React.ReactNode
}

export const AppHeader = ({ title, subtitle, search, actions }: AppHeaderProps) => {
  const { user, signOut } = useAuthStore()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleSignOut = async () => {
    handleCloseMenu()
    await signOut()
    navigate('/login')
  }

  const userInitials = getInitials(user?.displayName, user?.email)

  return (
    <Box variant='stickyHeader'>
      <Box variant='pageRow'>
        <Box variant='flex' gap='md'>
          <Kenneth size={64} />
          {(title || subtitle) && (
            <Box>
              {title && <Typography variant='h4'>{title}</Typography>}
              {subtitle && (
                <Typography variant='body2' color='text.secondary'>
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {search && <SearchColumn>{search}</SearchColumn>}

        <Box variant='flex'>
          {actions}
          <IconButton onClick={handleOpenMenu} size='small'>
            <Avatar variant='userAvatar' src={user?.photoURL ?? undefined}>
              {userInitials}
            </Avatar>
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { variant: 'selectMenu' } }}
      >
        <UserMenuHeader>
          <Typography variant='subtitle2'>{user?.displayName ?? 'User'}</Typography>
          <Typography variant='caption' color='text.secondary'>
            {user?.email}
          </Typography>
        </UserMenuHeader>
        <Divider />
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon fontSize='small' />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </Box>
  )
}
